import { useTranslation } from 'react-i18next'
import type { MonthlyEnergyDto } from '../../../dto/shared/SolarRequestDto'
import { formatKwh } from '../../../utils/format'

interface MonthlyEnergyChartProps {
  data: MonthlyEnergyDto[]
}

/** Fixed non-leap-year reference date, only used to resolve localized month abbreviations. */
function monthLabel(month: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2026, month - 1, 1))
}

/**
 * Monatliches Säulendiagramm für die Zusammenfassung: 12 Balken (PV-Produktion je Monat),
 * jeder unterteilt in direkt selbst verbrauchten PV-Strom, aus der Batterie gedeckten Verbrauch
 * und den Rest (Einspeisung/ungenutzt) - reiner Div/Flex-Balken wie ConsumptionBreakdownChart,
 * kein Chart-Framework im Projekt.
 */
export function MonthlyEnergyChart({ data }: MonthlyEnergyChartProps) {
  const { t, i18n } = useTranslation()

  if (data.length === 0) return null

  const maxProductionKwh = Math.max(...data.map((m) => m.pvProductionKwh), 1)
  const totalProductionKwh = data.reduce((sum, m) => sum + m.pvProductionKwh, 0)

  return (
    <div className="monthly-energy-chart">
      <div
        className="monthly-energy-chart-bars"
        role="img"
        aria-label={t('monthlyEnergyChart.ariaLabel', { total: formatKwh(totalProductionKwh) })}
      >
        {data.map((month) => {
          const direct = month.directPvConsumptionKwh
          const battery = month.batteryConsumptionKwh
          const remainder = Math.max(0, month.pvProductionKwh - direct - battery)
          const barHeightPct = (month.pvProductionKwh / maxProductionKwh) * 100
          const toSegmentPct = (kwh: number) => (kwh / month.pvProductionKwh) * 100 || 0

          return (
            <div className="monthly-energy-chart-column" key={month.month}>
              <span className="monthly-energy-chart-value">{Math.round(month.pvProductionKwh)}</span>
              <div
                className="monthly-energy-chart-bar"
                style={{ height: `${barHeightPct}%` }}
                title={t('monthlyEnergyChart.tooltip', {
                  month: monthLabel(month.month, i18n.language),
                  production: formatKwh(month.pvProductionKwh),
                  direct: formatKwh(direct),
                  battery: formatKwh(battery),
                })}
              >
                <div
                  className="monthly-energy-chart-segment monthly-energy-chart-segment-remainder"
                  style={{ height: `${toSegmentPct(remainder)}%` }}
                />
                <div
                  className="monthly-energy-chart-segment monthly-energy-chart-segment-battery"
                  style={{ height: `${toSegmentPct(battery)}%` }}
                />
                <div
                  className="monthly-energy-chart-segment monthly-energy-chart-segment-direct"
                  style={{ height: `${toSegmentPct(direct)}%` }}
                />
              </div>
              <span className="monthly-energy-chart-month-label">{monthLabel(month.month, i18n.language)}</span>
            </div>
          )
        })}
      </div>

      <ul className="monthly-energy-chart-legend">
        <li>
          <span className="monthly-energy-chart-swatch monthly-energy-chart-swatch-direct" />
          {t('monthlyEnergyChart.direct')}
        </li>
        <li>
          <span className="monthly-energy-chart-swatch monthly-energy-chart-swatch-battery" />
          {t('monthlyEnergyChart.battery')}
        </li>
        <li>
          <span className="monthly-energy-chart-swatch monthly-energy-chart-swatch-remainder" />
          {t('monthlyEnergyChart.remainder')}
        </li>
      </ul>

      {/* .sr-only clips via a 1px/1px box (siehe forms-and-auth.css) - unter table-layout:auto
          ignoriert eine <table> diese Breite aber und rendert stattdessen an ihrer
          Content-Mindestbreite (mehrere hundert Pixel), was auf schmalen Viewports horizontales
          Scrollen der ganzen Seite auslöst. Die Klasse gehört daher auf einen umschließenden div,
          nicht direkt auf die Tabelle. */}
      <div className="sr-only">
        <table>
          <caption>{t('monthlyEnergyChart.ariaLabel', { total: formatKwh(totalProductionKwh) })}</caption>
          <thead>
            <tr>
              <th>{t('monthlyEnergyChart.tableMonth')}</th>
              <th>{t('monthlyEnergyChart.tableProduction')}</th>
              <th>{t('monthlyEnergyChart.direct')}</th>
              <th>{t('monthlyEnergyChart.battery')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((month) => (
              <tr key={month.month}>
                <td>{monthLabel(month.month, i18n.language)}</td>
                <td>{formatKwh(month.pvProductionKwh)}</td>
                <td>{formatKwh(month.directPvConsumptionKwh)}</td>
                <td>{formatKwh(month.batteryConsumptionKwh)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
